import ExcelJS from 'exceljs';
import { IAnalyticsData, ICostDetail, ITimeDetail } from '../types';
import i18next from "i18next";
import { formatNumber } from './formatValue';
import { createEmployeeTable, columnNumberToLetter } from './table';
import { translateCategorySlug } from "../../../functions/translateCategorySlug";

export interface IAnalyticsExportData {
	type: "projects" | "employees";
	list: IAnalyticsData[]
	period_list: string[]
	period_items: {name: string, value: string}[]
}

const accentColor = "EF3129", fontName = "Calibri";

export const handleDownloadFullAnalyticsData = async (data: IAnalyticsExportData, setPopup?: (args: {popup: string, data: string}) => void) => {
	const workbook = new ExcelJS.Workbook(), type = data.type, { t } = i18next;
	const worksheetHours = workbook.addWorksheet(t("analytics_export.Hours"));

	let list = data.list;

	if (type === "employees") {
		let resultList = [] as IAnalyticsData[], names: string[] = [];
		list.forEach((item) => {
			names.push(item.name);
		})
		list.forEach((item) => {
			if (item.time_statistics) {
				item.time_statistics.forEach((subElement) => {
					if (!resultList.some(element => element.name === subElement.name)) {
						resultList.push({
							...item,
							name: subElement.name,
							time_statistics: [{ ...subElement, name: item.name }],
							cost_statistics: null
						})
					} else {
						resultList.filter(element => element.name === subElement.name)[0].time_statistics?.push({ ...subElement, name: item.name });
					}
				})
			}
			if (item.cost_statistics) {
				item.cost_statistics.forEach((subElement) => {
					if (!resultList.some(element => element.name === subElement.name)) {
						resultList.push({
							...item,
							name: subElement.name,
							cost_statistics: [{ ...subElement, name: item.name }]
						})
					} else {
						const targetItem = resultList.filter(element => element.name === subElement.name)[0];
						if (!targetItem.cost_statistics) {
							targetItem.cost_statistics = [];
						}
						targetItem.cost_statistics.push({ ...subElement, name: item.name });
					}
				})
			}
		})

		resultList.forEach(resultItem => {
			if (!resultItem.cost_statistics) {
				names.forEach(name => {
					if (!resultItem.cost_statistics) resultItem.cost_statistics = [];
					resultItem.cost_statistics.push({ name: name, value: -1, percentage: 0, color: "" });
				})
			} else {
				names.forEach(name => {
					if (resultItem.cost_statistics && !resultItem.cost_statistics.some(element => element.name === name)) {
						resultItem.cost_statistics.push({ name: name, value: -1, percentage: 0, color: "" });
					}
				})
			}
		})

		list = resultList;
	} else {
		let resultList = list as IAnalyticsData[], names: string[] = [];

		list.forEach((item) => {
			if (item.time_statistics) {
				item.time_statistics.forEach((subElement) => {
					if (!resultList.some(element => element.name === subElement.name)) {
						names.push(subElement.name)
					}
				})
			}
			if (item.cost_statistics) {
				item.cost_statistics.forEach((subElement) => {
					if (!resultList.some(element => element.name === subElement.name)) {
						names.push(subElement.name)
					}
				})
			}
		})

		list = resultList;
	}

	const createHeader = (worksheet: ExcelJS.Worksheet, page: string) => {
		worksheet.mergeCells('B2:H2');
		worksheet.getCell('B2').value = `${t("analytics_export.title")} (${page})`;
		worksheet.getCell('B2').font = { name: fontName, size: 22, bold: true };
		worksheet.getCell('B2').alignment = { horizontal: "left", vertical: 'middle' };
	};

	function setCellBorder(worksheet: ExcelJS.Worksheet, cellAddress: string | number, cellLastIndex?: number) {
		if (cellLastIndex) {
			worksheet.getCell(cellAddress, cellLastIndex).border = {
				top: { style: 'thin', color: { argb: '000000' } },
				left: { style: 'thin', color: { argb: '000000' } },
				bottom: { style: 'thin', color: { argb: '000000' } },
				right: { style: 'thin', color: { argb: '000000' } },
			};
		} else {
			worksheet.getCell(cellAddress).border = {
				top: { style: 'thin', color: { argb: '000000' } },
				left: { style: 'thin', color: { argb: '000000' } },
				bottom: { style: 'thin', color: { argb: '000000' } },
				right: { style: 'thin', color: { argb: '000000' } },
			};
		}
	}

	const createField = (worksheet: ExcelJS.Worksheet, item_1: string, item_2: string, row: number, col: number) => {
		worksheet.mergeCells([row, col, row, col + 3]);
		worksheet.getCell(row, col).value = item_1;
		worksheet.getCell(row, col).font = { name: fontName, size: 12 };
		worksheet.getCell(row, col).alignment = { vertical: "top", wrapText: true };

		worksheet.mergeCells([row, col + 4, row, col + 6]);
		worksheet.getCell(row, col + 4).value = item_2;
		worksheet.getCell(row, col + 4).font = { name: fontName, size: 12 };
		worksheet.getCell(row, col + 4).alignment = { vertical: "top", wrapText: true };
	};

	workbook.creator = 'IC-App';
	workbook.lastModifiedBy = 'IC-App';
	workbook.properties.date1904 = false;

	createHeader(worksheetHours, t(`analytics_export.Hours`));

	const createTable = (worksheet: ExcelJS.Worksheet, page: 'Hours' | 'Costs') => {
		const startCell = 3, lastCell = startCell + 1 + list.length,
			formatToFixed = page === "Hours" ? 1 : 2, zeroValue = formatNumber(0, formatToFixed);

		let startRow = 7;

		const dataObject: Record<string, Record<string, number>> = {};

		startRow = 7 + data.list.length;

		worksheet.getColumn(5).width = 11;
		worksheet.getColumn(6).width = 11;
		worksheet.getColumn(7).width = 11;
		worksheet.getColumn(8).width = 11;

		for (let index = startCell; index <= lastCell; index++) {
			worksheet.getColumn(index).width = 11;
		}
		
		data.list.forEach((item, index) => {
			createField(worksheet, `${index + 1} - ${item.name}`, `${index + 1} - ${data.period_list[index]}`, 5 + index, 2);
		})

		if (page === "Hours") {
			list.forEach((item) => {
				if (item.time_statistics) {
					item.time_statistics.forEach((element) => {
						if (!dataObject[element.name]) dataObject[element.name] = {};
						dataObject[element.name][item.name] = element.value;
					})
				}
			})
		} else {
			list.forEach((item) => {
				if (item.cost_statistics) {
					item.cost_statistics.forEach((element) => {
						if (!dataObject[element.name]) dataObject[element.name] = {};
						dataObject[element.name][item.name] = element.value;
					})
				}
			})
		}

		worksheet.getColumn('B').width = 5;
		worksheet.getColumn('C').width = 25;

		worksheet.mergeCells(`B${startRow}:C${startRow}`);
		worksheet.getCell(`B${startRow}`).value = 'iC Ukraine Internal';
		worksheet.getCell(`B${startRow}`).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
		worksheet.getCell(`B${startRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
		worksheet.getCell(`B${startRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
		setCellBorder(worksheet, `B${startRow}:C${startRow}`);

		worksheet.getCell(`B${startRow + 1}`).value = 'No';
		worksheet.getCell(`B${startRow + 1}`).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
		worksheet.getCell(`B${startRow + 1}`).alignment = { horizontal: 'center', vertical: 'middle' };
		worksheet.getCell(`B${startRow + 1}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
		setCellBorder(worksheet, `B${startRow + 1}`);

		worksheet.getCell(`C${startRow + 1}`).value = t("analytics_export.users");
		worksheet.getCell(`C${startRow + 1}`).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
		worksheet.getCell(`C${startRow + 1}`).alignment = { horizontal: 'center', vertical: 'middle' };
		worksheet.getCell(`C${startRow + 1}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
		setCellBorder(worksheet, `C${startRow + 1}`);

		list.forEach((item, index) => {
            const isProjectName = item.name && item.name.indexOf('_') !== -1;
            const projectName = isProjectName ? item.name.split("_")[0] : '';
            const projectDesc = isProjectName ? item.name.slice(projectName.length + 1, item.name.length) : item.name;

            worksheet.getCell(startRow, startCell + 1 + index).value = projectName || (isProjectName ? projectName : translateCategorySlug(t, item.name));
            worksheet.getCell(startRow, startCell + 1 + index).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
            worksheet.getCell(startRow, startCell + 1 + index).alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getCell(startRow, startCell + 1 + index).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
            setCellBorder(worksheet, startRow, startCell + 2 + index);

            worksheet.getCell(startRow + 1, startCell + 1 + index).value = projectDesc;
			worksheet.getCell(startRow + 1, startCell + 1 + index).alignment = { wrapText: true, horizontal: 'center', vertical: 'middle' };
			worksheet.getCell(startRow + 1, startCell + 1 + index).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
			setCellBorder(worksheet, startRow + 1, startCell + 1 + index);
		})

		worksheet.getCell(startRow, lastCell).value = page === "Hours" ? t("analytics_export.Hours") : t("analytics_export.Cost");
		worksheet.getCell(startRow, lastCell).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
		worksheet.getCell(startRow, lastCell).alignment = { horizontal: 'center', vertical: 'middle' };
		worksheet.getCell(startRow, lastCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
		setCellBorder(worksheet, startRow, lastCell);

		worksheet.getCell(startRow + 1, lastCell).value = t("analytics_export.total");
		worksheet.getCell(startRow + 1, lastCell).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
		worksheet.getCell(startRow + 1, lastCell).alignment = { horizontal: 'center', vertical: 'middle' };
		worksheet.getCell(startRow + 1, lastCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
		setCellBorder(worksheet, startRow + 1, lastCell);

		let listIndex = 0;
		const total: number[] = [];

		for (const key in dataObject) {
			let totalValue = 0;
			list.forEach((item) => {
				totalValue += dataObject[key][item.name] === -1 ? 0 : dataObject[key][item.name] ? dataObject[key][item.name] : 0;
			})

			total.push(totalValue);
		}

		for (const key in dataObject) {
			const rowIdx = startRow + 2 + listIndex, cell = startCell - 1;
			// if (key.length >= 15) worksheet.getRow(rowIdx).height = 37.5;

			worksheet.getCell(rowIdx, cell).value = listIndex + 1;
			worksheet.getCell(rowIdx, cell).alignment = { horizontal: 'center', vertical: 'middle' };
			worksheet.getCell(rowIdx, cell).font = { name: fontName, size: 10 };
			worksheet.getCell(rowIdx, cell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "ebebeb" } };
			worksheet.getCell(rowIdx, cell).border = {
				top: { style: 'hair', color: { argb: '000000' } },
				bottom: { style: 'hair', color: { argb: '000000' } },
				left: { style: 'thin', color: { argb: '000000' } },
			};

			// worksheet.getCell(rowIdx, cell + 1).value = key.length >= 15 ? key.replace(" ", "\n") : key;
			worksheet.getCell(rowIdx, cell + 1).value = key;
			worksheet.getCell(rowIdx, cell + 1).font = { name: fontName, size: 10 };
			worksheet.getCell(rowIdx, cell + 1).alignment = { wrapText: true, vertical: 'middle' };
			worksheet.getCell(rowIdx, cell + 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "ebebeb" } };
			worksheet.getCell(rowIdx, cell + 1).border = {
				top: { style: 'hair', color: { argb: '000000' } },
				bottom: { style: 'hair', color: { argb: '000000' } },
				right: { style: 'hair', color: { argb: '000000' } },
				left: { style: 'hair', color: { argb: '000000' } },
			};

			let dataIndex = 2;

			list.forEach((item) => {
				worksheet.getCell(rowIdx, cell + dataIndex).value = Number(dataObject[key][item.name] === -1 ? zeroValue : dataObject[key][item.name] ? formatNumber(dataObject[key][item.name], formatToFixed) : zeroValue);
				worksheet.getCell(rowIdx, cell + dataIndex).numFmt = `0.${"0".repeat(formatToFixed)}`;
				worksheet.getCell(rowIdx, cell + dataIndex).font = { name: fontName, size: 10, bold: dataObject[key][item.name] !== -1 && dataObject[key][item.name] > 0 ? true : false };
				worksheet.getCell(rowIdx, cell + dataIndex).alignment = { horizontal: "center", vertical: 'middle' };
				worksheet.getCell(rowIdx, cell + dataIndex).border = {
					top: { style: 'hair', color: { argb: '000000' } },
					bottom: { style: 'hair', color: { argb: '000000' } },
					right: { style: 'hair', color: { argb: '000000' } },
				};

				dataIndex++;
			})

			const startCellName = columnNumberToLetter(4),
			endCellName = columnNumberToLetter(cell + dataIndex - 1);

			//worksheet.getCell(rowIdx, cell + dataIndex).value = Number(total[listIndex] ? formatNumber(total[listIndex], formatToFixed) : zeroValue);
			worksheet.getCell(rowIdx, cell + dataIndex).value = { formula: `SUM(${startCellName}${rowIdx}:${endCellName}${rowIdx})`, result: String(total[listIndex] ? formatNumber(total[listIndex], formatToFixed) : zeroValue) };
			worksheet.getCell(rowIdx, cell + dataIndex).numFmt = `0.${"0".repeat(formatToFixed)}`;
			worksheet.getCell(rowIdx, cell + dataIndex).font = { name: fontName, size: 10, bold: total[listIndex] ? true : false };
			worksheet.getCell(rowIdx, cell + dataIndex).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "ebebeb" } };
			worksheet.getCell(rowIdx, cell + dataIndex).alignment = { horizontal: "center", vertical: 'middle' };
			worksheet.getCell(rowIdx, cell + dataIndex).border = {
				top: { style: 'hair', color: { argb: '000000' } },
				bottom: { style: 'hair', color: { argb: '000000' } },
				right: { style: 'thin', color: { argb: '000000' } },
			};

			listIndex++;
		}

		const lastRow = startRow + 2 + total.length;
		worksheet.mergeCells([lastRow, startCell - 1, lastRow, startCell]);

		worksheet.getCell(lastRow, startCell).value = page === "Hours" ? t("analytics_export.total_hours") : t("analytics_export.total_cost");
		worksheet.getCell(lastRow, startCell).font = { name: fontName, size: 10, bold: true };
		worksheet.getCell(lastRow, startCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'c7c7c7' } };
		worksheet.getCell(lastRow, startCell).alignment = { horizontal: 'center', vertical: 'middle' };

		list.forEach((item, index) => {
			let totalValue = 0;
			for (const key in dataObject) {
				totalValue = totalValue + (dataObject[key][item.name] === -1 ? 0 : dataObject[key][item.name] || 0);
			}

			const startCellName = columnNumberToLetter(startCell + 1 + index);

			worksheet.getCell(lastRow, startCell + 1 + index).value = totalValue ? { formula: `SUM(${startCellName}${startRow + 2}:${startCellName}${lastRow-1})`, result: String(formatNumber(totalValue, formatToFixed)) } : 0;
			worksheet.getCell(lastRow, startCell + 1 + index).numFmt = `0.${"0".repeat(formatToFixed)}`;
			worksheet.getCell(lastRow, startCell + 1 + index).font = { name: fontName, size: 10, color: { argb: "000000" }, bold: true };
			worksheet.getCell(lastRow, startCell + 1 + index).alignment = { horizontal: 'center', vertical: 'middle' };
			worksheet.getCell(lastRow, startCell + 1 + index).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "c7c7c7" } };
			setCellBorder(worksheet, lastRow, startCell + 1 + index);
		})

		const totalValue = total.reduce((sum, value) => sum + value, 0);
		const cellName = columnNumberToLetter(lastCell);

		worksheet.getCell(lastRow, lastCell).value = totalValue ? { formula: `SUM(${cellName}${startRow + 2}:${cellName}${lastRow-1})`, result: String(totalValue ? formatNumber(totalValue, formatToFixed) : zeroValue) } : 0;
		worksheet.getCell(lastRow, lastCell).numFmt = `0.${"0".repeat(formatToFixed)}`;
		worksheet.getCell(lastRow, lastCell).font = { name: fontName, size: 10, bold: true, color: { argb: accentColor } };
		worksheet.getCell(lastRow, lastCell).alignment = { horizontal: 'center', vertical: 'middle' };
		worksheet.getCell(lastRow, lastCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'c7c7c7' } };

		setCellBorder(worksheet, lastRow, startCell);
		setCellBorder(worksheet, lastRow, lastCell);
	};

	createTable(worksheetHours, "Hours");

	const worksheetCosts = workbook.addWorksheet(t("analytics_export.Costs"));
	createHeader(worksheetCosts, t("analytics_export.Cost"));
	createTable(worksheetCosts, "Costs");

	const employees: string[] = [];
	list.forEach(data => {
		data.time_statistics && data.time_statistics.forEach(item => {
			if(!employees.includes(item.name)) {
				employees.push(item.name);
			}
		});
	
		data.cost_statistics && data.cost_statistics.forEach(item => {
			if(!employees.includes(item.name)) {
				employees.push(item.name);
			}
		})
	})
	
	if(employees.length) {
		const createField = (worksheet: ExcelJS.Worksheet, label: string, value: string, row: number, isWrap?: boolean) => {
			worksheet.getColumn("C").width = 15;
		
			worksheet.mergeCells(`B${row}:C${row}`);
			worksheet.getCell(`B${row}`).value = label;
			worksheet.getCell(`B${row}`).font = { name: fontName, size: 14, bold: true };
			worksheet.getCell(`B${row}`).alignment = { vertical: 'middle' };
		
			worksheet.getCell(`D${row}`).value = value;
			worksheet.getCell(`D${row}`).font = { name: fontName, size: 12 };
			worksheet.getCell(`D${row}`).alignment = { vertical: 'middle', ...isWrap ? { wrapText: true, vertical: "middle" } : null };

			if(isWrap) worksheet.getRow(row).height = 12 * value.split("\n").length;
		};

		employees.forEach(name => {
			const worksheet = workbook.addWorksheet(name);

			worksheet.mergeCells([4, 4, 4, 4 + 2]);
			createField(worksheet, t("employees_admin.table.employee") + ":", name, 4);

			const periodData = data.period_items.filter(item => item.name === name)[0]

			worksheet.mergeCells([5, 4, 5, 4 + 2]);
			createField(worksheet, t("analytics_export.report_period") + ":", periodData.value, 5);

			worksheet.getColumn("C").width = 9;

			let timeDetails: ITimeDetail[] = [],
				costDetails: ICostDetail[] = [];

			list.forEach(item => {
				timeDetails = timeDetails.concat(item.time_details);
				costDetails = costDetails.concat(item.cost_details);

				timeDetails = timeDetails.filter((item, index) => timeDetails.indexOf(item) === index && item.user_name === name);
				costDetails = costDetails.filter((item, index) => costDetails.indexOf(item) === index && item.user_name === name);
			})
	
			createEmployeeTable(worksheet, {
				time: timeDetails,
				cost: costDetails
			})
		})
	}

	workbook.xlsx.writeBuffer().then((buffer) => {
		const blob = new Blob([buffer], { type: 'application/octet-stream' }),
			date = new Date(), year = String(date.getFullYear()).slice(2, 4), month = date.getMonth() + 1 > 9 ? String(date.getMonth() + 1) : `0${date.getMonth() + 1}`,
			day = date.getDate() > 9 ? String(date.getDate()) : `0${date.getDate()}`;

		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = `${year}${month}${day}_Statistic_Report_Full.xlsx`;
		link.click();
		link.remove();

		if(setPopup) setPopup({popup: "", data: ""});
	});
};